"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { type DSLinkProps, useAdapters } from "@/lib/adapters";
import { cn } from "@/lib/utils";

const linkVariants = cva(
  "font-medium underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-link rounded-sm",
  {
    variants: {
      variant: {
        primary: "text-link",
        secondary: "text-link-secondary",
        footer: "text-link-footer hover:text-white",
      },
    },
    defaultVariants: { variant: "primary" },
  },
);

export interface LinkProps
  extends Omit<DSLinkProps, "className">,
    VariantProps<typeof linkVariants> {
  className?: string;
  external?: boolean;
}

/**
 * O link do sistema.
 *
 * Renderiza o `Link` do adapter, que é `<a>` por padrão e vira `next/link` num
 * app Next — é o que mantém o prefetch de quem tem roteador sem obrigar quem
 * não tem. `external` abre em aba nova com o `rel` que isso exige.
 */
export function Link({ className, variant, external, children, href, ...props }: LinkProps) {
  const { Link: LinkImpl } = useAdapters();
  const externalProps = external ? { target: "_blank" as const, rel: "noopener noreferrer" } : {};
  return (
    <LinkImpl
      href={href}
      className={cn(linkVariants({ variant, className }))}
      {...externalProps}
      {...props}
    >
      {children}
    </LinkImpl>
  );
}

export { linkVariants };
